import React from "react";
import "./Home.css";

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import transition from "../../transition";

import RevealHeading from "../shared/RevealHeading";
import PortraitReveal from "../shared/PortraitReveal";
import Stack from "../Stack/Stack";
import projects from "../../data/projects";
import Seo from "../../seo/Seo";
import { personJsonLd, websiteJsonLd } from "../../seo/site";
import { useLang, useTranslations, langPath } from "../../i18n";
import { localizeProject } from "../../i18n/projectContent";

const ease = [0.83, 0, 0.17, 1];

const Home = () => {
  const lang = useLang();
  const t = useTranslations();
  const featured = projects.slice(0, 5).map((p) => localizeProject(p, lang));

  return (
    <motion.div className="Home">
      <Seo
        lang={lang}
        path=""
        description={t.seo.homeDesc}
        jsonLd={[personJsonLd, websiteJsonLd]}
      />
      <div className="bg"></div>
      <h1 className="visually-hidden">
        {t.siteName}, {t.jobTitle}
      </h1>
      <section className="hero">
        <div className="headers">
          <div className="header header-1">
            <RevealHeading delay={-0.25}>{t.home.name1}</RevealHeading>
            <RevealHeading delay={-0.1}>&nbsp;{t.home.name2}</RevealHeading>
            <PortraitReveal className="hero-portrait" delay={0.1} />
          </div>
          <div className="header header-2">
            <RevealHeading delay={-0.25}>{t.home.role1}</RevealHeading>
            <RevealHeading delay={-0.1}>&nbsp;{t.home.role2}</RevealHeading>
          </div>
        </div>
        <div className="footer">
          <motion.div
            className="divider"
            initial={{ width: 0, transition: { duration: 1, ease } }}
            animate={{ width: "100%", transition: { duration: 1.5, ease } }}
          ></motion.div>
          <motion.div
            className="footer-content"
            initial={{
              y: 200,
              opacity: 0,
              transition: { duration: 1, ease, delay: 2 },
            }}
            animate={{
              y: 0,
              opacity: 1,
              transition: { duration: 2, ease },
            }}
          >
            <div className="footer-col">
              <div className="arrow">
                <p>&darr;</p>
              </div>
              <div className="arrow">
                <p>&darr;</p>
              </div>
            </div>
            <div className="footer-col">
              <p>
                {t.home.basedIn} <br /> {t.home.casablanca}
              </p>
            </div>
            <div className="footer-col">
              <p>
                {t.home.availableFor} <br /> {t.home.freelanceWork}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="projects-nav">
        <div className="projects-nav-container">
          <div className="projects-nav-label">
            <p>{t.home.selectedWork}</p>
          </div>
          {featured.map((project) => (
            <div className="project-item" key={project.slug}>
              <Link to={langPath(`/projects/${project.slug}`, lang)}>
                <div className="project-link">
                  <div className="project-l">
                    <div className="project-link-img">
                      <img
                        src={project.image}
                        alt={project.name}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="project-name">
                      <h2>{project.name}</h2>
                    </div>
                  </div>
                  <div className="project-date">
                    <p>{project.category}</p>
                  </div>
                  <div className="project-dir">
                    <p>&#8599;</p>
                  </div>
                </div>
              </Link>
            </div>
          ))}
          <div className="projects-nav-all">
            <Link to={langPath("/projects", lang)}>
              {t.home.viewAllWork} &#8599;
            </Link>
          </div>
        </div>
      </div>

      <section className="about">
        <div className="about-container">
          <div className="about-col">
            <p>{t.home.aboutLabel}</p>
            <p>{t.home.aboutTeaser}</p>
          </div>
          <div className="about-col">
            <div className="socials">
              <a href="mailto:elmajdoubmounir@gmail.com">
                {t.home.email} &#8599;
              </a>
              <a
                href="https://www.linkedin.com/in/elmajdoubmounir/"
                target="_blank"
                rel="noreferrer"
              >
                {t.home.linkedin} &#8599;
              </a>
              <a
                href="https://www.instagram.com/mejdubmunir"
                target="_blank"
                rel="noreferrer"
              >
                {t.home.instagram} &#8599;
              </a>
            </div>
          </div>
        </div>
      </section>

      <Stack />

      <div className="footer">
        <div className="divider"></div>
        <div className="footer-content">
          <div className="footer-col">
            <div className="arrow">
              <p>&#8593;</p>
            </div>
            <div className="arrow">
              <p>&#8593;</p>
            </div>
          </div>
          <div className="footer-col">
            <p>
              {t.home.copy} <br /> {t.home.footerName}
            </p>
          </div>
          <div className="footer-col">
            <p>{t.home.footerTitle}</p>
          </div>
          <div className="footer-col">
            <p>
              {t.home.basedIn} <br /> {t.home.footerBased}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default transition(Home);
